import { useAtomValue, useSetAtom } from 'jotai';
import type { PopupOptions } from 'maplibre-gl';
import { useEffect, useEffectEvent, useRef, useState, type ReactNode, type Ref, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { flightDuration, MapPopup, useMap } from '@/features/map';
import { useUnitFormatter } from '@/features/units';
import type { Station } from '../model/types';
import { selectedStationAtom, selectedStationIdAtom } from '../state/atoms';
import {
  DensityAltitudeIcon,
  ElevationIcon,
  GustIcon,
  LocationIcon,
  PrecipitationIcon,
  TemperatureIcon,
  WindIcon,
} from './icons';
import { MARKER_RADIUS } from './markerImage';
import { useWeatherText } from './useWeatherText';

/** Gap between the circle and the popup's tip, px. */
const POPUP_OFFSET = MARKER_RADIUS + 4;

const OPTIONS: PopupOptions = {
  closeOnClick: false, // map clicks are handled by StationLayer, so tapping another station switches to it
  offset: POPUP_OFFSET,
  maxWidth: '290px',
};

const COMPASS = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
];
const compass = (deg: number) => COMPASS[Math.round(deg / 22.5) % 16];

/**
 * One detail on a single line, cut short with an ellipsis if too long; left out when there is no value.
 * Each row is laid out on its own, so a long label on one row doesn't squeeze the value on another.
 */
function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  if (children === null || children === undefined) return null;
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="flex shrink-0 items-center gap-2 whitespace-nowrap text-hint">
        {icon}
        {label}
      </dt>
      <dd className="min-w-0 truncate text-right font-medium">{children}</dd>
    </div>
  );
}

/** Small arrow pointing downwind, like the one on the map. */
function WindArrow({ from }: { from: number }) {
  return (
    <svg
      className="mr-1 inline-block size-3.5 align-[-2px] text-neutral-500"
      viewBox="0 0 16 16"
      style={{ rotate: `${from + 180}deg` }}
      aria-hidden
    >
      <path d="M8 1 13 14 8 11 3 14z" fill="currentColor" />
    </svg>
  );
}

/** "12 min ago", kept current while shown. */
function useTimeAgo(time: number) {
  const { i18n } = useTranslation();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.max(0, Math.round((now - time) / 60_000));
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto', style: 'short' });
  return minutes < 60 ? rtf.format(-minutes, 'minute') : rtf.format(-Math.round(minutes / 60), 'hour');
}

function Details({ station: s, ref }: { station: Station; ref: Ref<HTMLDivElement> }) {
  const { t } = useTranslation('stations');
  const fmt = useUnitFormatter();
  const ago = useTimeAgo(s.time);
  const weather = useWeatherText(s);
  const directed = s.windDirection !== null && s.windSpeed >= 0.5;
  const da = s.densityAltitude;

  return (
    <div ref={ref} className="text-sm tabular-nums">
      <h2 className="pr-8 text-[15px] leading-snug font-semibold">{s.name}</h2>
      <p className="text-xs text-hint">
        {s.id} · <time dateTime={new Date(s.time).toISOString()}>{ago}</time>
      </p>
      <dl className="mt-2 flex flex-col gap-y-1">
        <Row icon={<WindIcon />} label={t('details.wind')}>
          {directed ? (
            <>
              <WindArrow from={s.windDirection!} />
              {t('details.windFrom', { speed: fmt.speed(s.windSpeed), dir: compass(s.windDirection!) })}
            </>
          ) : (
            fmt.speed(s.windSpeed)
          )}
        </Row>
        <Row icon={<GustIcon />} label={t('details.gust')}>
          {s.windGust !== null && fmt.speedValue(s.windGust) > fmt.speedValue(s.windSpeed)
            ? fmt.speed(s.windGust)
            : null}
        </Row>
        <Row icon={<TemperatureIcon />} label={t('details.temperature')}>
          {s.airTemp === null ? null : fmt.temperature(s.airTemp)}
        </Row>
        <Row icon={<DensityAltitudeIcon />} label={t('details.densityAltitude')}>
          {da === null
            ? null
            : s.densityAltitudeEstimated
              ? t('details.estimated', { value: fmt.height(da) })
              : fmt.height(da)}
        </Row>
        <Row icon={<PrecipitationIcon weather={s.weather} />} label={t('details.precipitation')}>
          {weather}
        </Row>
        <Row icon={<ElevationIcon />} label={t('details.elevation')}>
          {s.elevation === null ? null : fmt.height(s.elevation)}
        </Row>
        <Row icon={<LocationIcon />} label={t('details.coordinates')}>
          {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
        </Row>
      </dl>
    </div>
  );
}

/**
 * Pans the opened station to the middle of the map, a little below it so the station and its popup
 * above it are centred together. The panels are ignored: centring in the map area they leave looks off.
 */
function useCenterOnOpen(station: Station | null, details: RefObject<HTMLDivElement | null>) {
  const map = useMap();
  const center = useEffectEvent(() => {
    const popup = details.current?.closest<HTMLElement>('.maplibregl-popup');
    if (!map || !station || !popup) return;
    // From the popup's top to the circle's bottom edge.
    const down = (popup.offsetHeight + POPUP_OFFSET - MARKER_RADIUS) / 2;
    const center: [number, number] = [station.lng, station.lat];
    map.easeTo({ center, offset: [0, down], duration: flightDuration(map, center) });
  });
  const id = station?.id;
  useEffect(() => {
    if (id === undefined) return;
    // The popup joins the map in MapPopup's effect; measure it once it is laid out.
    const frame = requestAnimationFrame(center);
    return () => cancelAnimationFrame(frame);
  }, [id]);
}

/** Details of the tapped station; Escape or the × closes it. */
export function StationPopup() {
  const station = useAtomValue(selectedStationAtom);
  const setSelected = useSetAtom(selectedStationIdAtom);
  const details = useRef<HTMLDivElement>(null);
  useCenterOnOpen(station, details);

  useEffect(() => {
    if (!station) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelected(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [station, setSelected]);

  if (!station) return null;
  return (
    <MapPopup
      key={station.id}
      lng={station.lng}
      lat={station.lat}
      open
      options={OPTIONS}
      onClose={() => setSelected(null)}
    >
      <Details station={station} ref={details} />
    </MapPopup>
  );
}
